import React from 'react';
import { Menu, Button, Space, Tooltip } from 'antd';
import { useRef } from 'react';
import {
  ThunderboltOutlined,
  RobotOutlined,
  FormOutlined,
  SendOutlined,
  SaveOutlined,
  PlayCircleOutlined,
  MailOutlined,
  BranchesOutlined,
  FileAddOutlined,
  BarChartOutlined,
  FileTextOutlined,
  FileSearchOutlined,
  EditOutlined,
  LineChartOutlined,
  FontSizeOutlined,
  CopyOutlined,
  SearchOutlined,
  FireOutlined,
  FileDoneOutlined,
  UserOutlined,
  ImportOutlined,
  MessageOutlined,
  ApiOutlined,
  PictureOutlined,
  PushpinOutlined,
  FileOutlined,
  ShopOutlined,
  VideoCameraOutlined,
  ClusterOutlined,
  CrownOutlined
} from '@ant-design/icons';
import { Video } from 'lucide-react';
import { isNodeAllowedInLite } from '../config/nodeConfig';

const { SubMenu } = Menu;

const Sidebar = ({ 
  updateNodeData,  
  onImport, 
  onSave, 
  onExecute, 
  onUpdate, 
  onSaveAsTemplate, 
  handleSaveEditedTemplate, 
  isEditMode, 
  isExecuting, 
  isEditingTemplate, 
  isSavedWorkflow, 
  workflowName, 
  onActionModalOpen,
  darkMode
}) => {
  const fileInputRef = useRef(null);

  const colors = {
    background: darkMode ? '#1f1f1f' : '#ffffff',
    border: darkMode ? '#333333' : '#f0f0f0',
    text: darkMode ? '#ffffff' : '#000000',
    menuBackground: darkMode ? '#141414' : '#ffffff',
    buttonBorder: darkMode ? '#177ddc' : '#1677ff',
    primaryButtonBg: darkMode ? '#177ddc' : '#1677ff',
    primaryButtonHoverBg: darkMode ? '#1e88e5' : '#4096ff',
    secondaryButtonBg: darkMode ? '#141414' : '#ffffff',
    secondaryButtonText: darkMode ? '#177ddc' : '#1677ff',
    secondaryButtonHoverBg: darkMode ? '#1f1f1f' : '#e6f4ff',
  };
  
  const onDragStart = (event, nodeType) => {
    const isLite = process.env.REACT_APP_VERSION === 'lite';
    
    // Always allow dragging
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.setData('isLocked', isLite && !isNodeAllowedInLite(nodeType));
    event.dataTransfer.effectAllowed = 'move';
  };

  const buttonStyle = {
    width: '100%',
    marginBottom: '8px',
    borderRadius: '6px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    border: `1px solid ${colors.buttonBorder}`,
    transition: 'all 0.2s ease',
    boxShadow: darkMode ? 'none' : '0 2px 0 rgba(0, 0, 0, 0.02)'
  };

  const primaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: colors.primaryButtonBg,
    color: '#ffffff',
    '&:hover': {
      backgroundColor: colors.primaryButtonHoverBg,
      borderColor: colors.primaryButtonHoverBg
    }
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    backgroundColor: colors.secondaryButtonBg,
    color: colors.secondaryButtonText,
    '&:hover': {
      backgroundColor: colors.secondaryButtonHoverBg,
      borderColor: colors.buttonBorder
    }
  };

  // Show execute button if workflow is saved or in edit mode, and not editing a template
  const showExecuteButton = (isSavedWorkflow || isEditMode || workflowName) && !isEditingTemplate;

  return (
    <div className="workflow-sidebar" style={{ 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      borderRight: `1px solid ${colors.border}`,
      background: colors.background,
      color: colors.text
    }}>
      <div className="sidebar-scroll" style={{ flex: 1, overflowY: 'auto' }}>

      <Menu 
        mode="vertical" 
        style={{ 
          width: '100%',
          border: 'none',
          background: colors.menuBackground,
          color: colors.text
        }}
        theme={darkMode ? 'dark' : 'light'}
      >
{/* TRIGGER NODE - Always available */}
        <Menu.Item 
          key="trigger" 
          icon={<ThunderboltOutlined />} 
          draggable 
          onDragStart={(event) => onDragStart(event, 'trigger')}
        >
          Trigger
        </Menu.Item>

        {/* AI ACTIONS SUBMENU */}
        <div className="ai-actions-menu">
          <SubMenu key="aiActions" icon={<RobotOutlined />} title="AI Actions">
            {/* AI Model - Lite allowed */}
            <Menu.Item 
              key="aiModel" 
              icon={<RobotOutlined />} 
              draggable
              onDragStart={(event) => onDragStart(event, 'aiModel')}
            >
              AI Model
            </Menu.Item>

            {/* Research - Lite allowed */}
            <Menu.Item 
              key="research" 
              icon={<SearchOutlined />} 
              draggable
              onDragStart={(event) => onDragStart(event, 'research')}
            >
              Research
            </Menu.Item>

            {/* PRO-ONLY AI NODES */}
            <Menu.Item 
              key="sentimentAnalysis" 
              icon={<BarChartOutlined />} 
              draggable
              onDragStart={(event) => onDragStart(event, 'sentimentAnalysis')}
            >
              Sentiment Analysis
              {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('sentimentAnalysis') && (
                <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
                  (PRO)
                </span>
              )}
            </Menu.Item>

            <Menu.Item 
              key="summaryGenerator" 
              icon={<FileTextOutlined />} 
              draggable
              onDragStart={(event) => onDragStart(event, 'summaryGenerator')}
            >
              Summary Generator
              {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('summaryGenerator') && (
                <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
                  (PRO)
                </span>
              )}
            </Menu.Item>

            <Menu.Item 
              key="extractInformation" 
              icon={<FileSearchOutlined />} 
              draggable
              onDragStart={(event) => onDragStart(event, 'extractInformation')}
            >
              Extract Information
              {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('extractInformation') && (
                <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
                  (PRO)
                </span>
              )}
            </Menu.Item>

            <Menu.Item 
              key="writeArticle" 
              icon={<FontSizeOutlined />} 
              draggable
              onDragStart={(event) => onDragStart(event, 'writeArticle')}
            >
              Write an Article
              {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('writeArticle') && (
                <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
                  (PRO)
                </span>
              )}
            </Menu.Item>

            <Menu.Item 
              key="optimizeSEO" 
              icon={<LineChartOutlined />} 
              draggable
              onDragStart={(event) => onDragStart(event, 'optimizeSEO')}
            >
              Optimize for SEO
              {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('optimizeSEO') && (
                <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
                  (PRO)
                </span>
              )}
            </Menu.Item>
          </SubMenu>
        </div>

        {/* MEDIA GENERATOR - Pro only */}
        <Menu.Item 
          key="mediaGenerator" 
          icon={<VideoCameraOutlined />} 
          draggable
          onDragStart={(event) => onDragStart(event, 'mediaGenerator')}
        >
          Media Generator
          {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('mediaGenerator') && (
            <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
              (PRO)
            </span>
          )}
        </Menu.Item>

        {/* WEB SCRAPER - Pro only */}
        <Menu.Item 
          key="firecrawl" 
          icon={<FireOutlined />} 
          draggable
          onDragStart={(event) => onDragStart(event, 'firecrawl')}
        >
          Web Scraper/Crawler
          {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('firecrawl') && (
            <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
              (PRO)
            </span>
          )}
        </Menu.Item>

        {/* MCP CLIENT - Pro only */}
        <Menu.Item 
          key="MCPClient" 
          icon={<ClusterOutlined />} 
          draggable
          onDragStart={(event) => onDragStart(event, 'MCPClient')}
        >
          MCP Clients
          {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('MCPClient') && (
            <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
              (PRO)
            </span>
          )}
        </Menu.Item>

        {/* DOCUMENT PARSER - Pro only */}
        <Menu.Item 
          key="parser" 
          icon={<FileDoneOutlined />} 
          draggable
          onDragStart={(event) => onDragStart(event, 'parser')}
        >
          Document Parser
          {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('parser') && (
            <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
              (PRO)
            </span>
          )}
        </Menu.Item>

        {/* HUMAN INPUT - Pro only */}
        <Menu.Item 
          key="humanInput" 
          icon={<UserOutlined />} 
          draggable
          onDragStart={(event) => onDragStart(event, 'humanInput')}
        >
          Human Input
          {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('humanInput') && (
            <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
              (PRO)
            </span>
          )}
        </Menu.Item>

        {/* CREATE POST - Lite allowed */}
        <Menu.Item 
          key="post" 
          icon={<PushpinOutlined />} 
          draggable
          onDragStart={(event) => onDragStart(event, 'post')}
        >
          Create Post
        </Menu.Item>

        {/* CREATE FILE - Pro only */}
        <Menu.Item 
          key="createFile" 
          icon={<FileAddOutlined />} 
          draggable
          onDragStart={(event) => onDragStart(event, 'createFile')}
        >
          Create File
          {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('createFile') && (
            <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
              (PRO)
            </span>
          )}
        </Menu.Item>

        {/* UNSPLASH IMAGE - Lite allowed */}
        <Menu.Item 
          key="unsplash" 
          icon={<PictureOutlined />} 
          draggable
          onDragStart={(event) => onDragStart(event, 'unsplash')}
        >
          Unsplash Image
        </Menu.Item>

        {/* SEND EMAIL - Pro only */}
        <Menu.Item 
          key="sendEmail" 
          icon={<MailOutlined />} 
          draggable
          onDragStart={(event) => onDragStart(event, 'sendEmail')}
        >
          Send Email
          {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('sendEmail') && (
            <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
              (PRO)
            </span>
          )}
        </Menu.Item>

        {/* CONDITION - Pro only */}
        <Menu.Item 
          key="condition" 
          icon={<BranchesOutlined />} 
          draggable
          onDragStart={(event) => onDragStart(event, 'condition')}
        >
          Condition
          {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('condition') && (
            <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
              (PRO)
            </span>
          )}
        </Menu.Item>

        {/* CHAT - Lite allowed (with restrictions) */}
        <Menu.Item 
          key="chat" 
          icon={<MessageOutlined />} 
          draggable
          onDragStart={(event) => onDragStart(event, 'chat')}
        >
          Chatbot
        </Menu.Item>

        {/* API CALL - Pro only */}
        <Menu.Item 
          key="APICall" 
          icon={<ApiOutlined />} 
          draggable
          onDragStart={(event) => onDragStart(event, 'APICall')}
        >
          API Call
          {process.env.REACT_APP_VERSION === 'lite' && !isNodeAllowedInLite('APICall') && (
            <span style={{ marginLeft: '8px', color: '#1890ff', fontSize: '11px', fontWeight: '500' }}>
              (PRO)
            </span>
          )}
        </Menu.Item>

        {/* OUTPUT - Lite allowed (with restrictions) */}
        <div className="node-selector-output">
          <Menu.Item 
            key="output" 
            icon={<SendOutlined />} 
            draggable
            onDragStart={(event) => onDragStart(event, 'output')}
          >
            Output
          </Menu.Item>
        </div>
      </Menu>
    </div>

      {/* Buttons section */}
      <div style={{ 
        padding: '16px', 
        borderTop: `1px solid ${colors.border}`,
        backgroundColor: colors.background
      }}>
        {/* Update/Save Workflow Button */}
        {isEditMode || isSavedWorkflow ? (
          <Button 
            icon={<EditOutlined />} 
            onClick={onUpdate}
            style={secondaryButtonStyle}
            className={`workflow-button ${darkMode ? 'dark' : ''}`}
          >
            Update Workflow
          </Button>
        ) : (
          <Button 
            icon={<SaveOutlined />} 
            onClick={onSave}
            type="primary"
            style={primaryButtonStyle}
            className={`save-workflow-button workflow-button ${darkMode ? 'dark' : ''}`}
          >
            Save Workflow
          </Button>
        )}

        {/* Execute Button */}
        {showExecuteButton && (
          <Button 
            icon={<PlayCircleOutlined />} 
            onClick={onExecute}
            type="primary"
            loading={isExecuting}
            disabled={isExecuting}
            style={primaryButtonStyle}
            className={`execute-workflow-button workflow-button ${darkMode ? 'dark' : ''}`}
          >
            {isExecuting ? 'Executing...' : 'Execute Workflow'}
          </Button>
        )}

        {/* Template Buttons */}
        {isEditingTemplate && (
          <Button 
            icon={<SaveOutlined />}
            onClick={handleSaveEditedTemplate}
            style={secondaryButtonStyle}
            className={`workflow-button ${darkMode ? 'dark' : ''}`}
          >
            Save Edited Template
          </Button>
        )}

        <Button 
          icon={<CopyOutlined />} 
          onClick={onSaveAsTemplate}
          style={secondaryButtonStyle}
          className={`workflow-button ${darkMode ? 'dark' : ''}`}
        >
          Save as Template
        </Button>

        <Button 
          icon={<ImportOutlined />} 
          onClick={onActionModalOpen}
          style={secondaryButtonStyle}
          className={`workflow-button ${darkMode ? 'dark' : ''}`}
        >
          Import/Export
        </Button>
      </div>
    </div>
  );
};

export default Sidebar;